import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import chroma from "chroma-js";
import countries from "i18n-iso-countries";
import MenuItem from "@material-ui/core/MenuItem";
import World from "../../../../assets/svg-maps/world.robinson";

import { formatDate } from "../../../../utilities";
import { colors, Select, LegendColor } from "../../../../theme";
import Map from "../../../dataviz/DataMap";
import { data, scales } from "./assets/data.json";
import policies from "./assets/policies.json";
import steps from "./assets/steps.json";

countries.registerLocale(require("i18n-iso-countries/langs/fr.json"));

const Container = styled.div`
  margin: 60px 0;

  @media (max-width: 1599px) {
    margin: 50px 0;
  }

  @media (max-width: 767px) {
    margin: 40px 0;
  }
`;

const Inputs = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;

  @media (max-width: 767px) {
    display: block;
    margin: 0;
  }

  > div {
    width: calc(50% - 25px);

    @media (max-width: 767px) {
      margin-bottom: 30px;
      width: 100%;
    }
  }
`;

const TooltipList = styled.ul`
  font-size: 1.5rem;
  list-style: inside;
  margin: 6px 0;
  padding: 0;

  li {
    margin-bottom: 4px;
  }
`;

const Legend = styled.div`
  color: ${colors.textColor};
  margin-top: 20px;

  @media (max-width: 767px) {
    margin-top: 10px;
  }

  h1 {
    color: black;
    font-size: 1.6rem;
    font-weight: 600;
    margin-bottom: 8px;
    text-align: center;

    @media (max-width: 767px) {
      text-align: left;
    }
  }

  p {
    font-size: 1.4rem;
    margin-bottom: 20px;
    text-align: center;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    font-size: 1.3rem;
    display: inline-block;
    line-height: 2.4rem;
    width: calc(100% / 3);

    @media (max-width: 1599px) {
      width: 50%;
    }

    @media (max-width: 767px) {
      width: 100%;
    }
  }
`;

const scaleColor = [
  colors.darkBlue,
  chroma(colors.blue).darken(0.25),
  chroma(colors.blue).brighten(0.75),
  chroma(colors.red).brighten(2),
  chroma(colors.red).darken(0.1),
];

function getDataKey(policy, step) {
  return `days_since_${step}_${policy}`;
}

function getLocationColor(countryId, dataKey) {
  const scale = scales[dataKey];
  const value = data[countryId] && data[countryId][dataKey];

  if (value === false || value === undefined) {
    return chroma(colors.borderColor).brighten(0.6);
  } else if (value === null) {
    return chroma(colors.borderColor).darken(0.15);
  } else if (value < scale[1]) {
    return scaleColor[0];
  } else if (value < scale[2]) {
    return scaleColor[1];
  } else if (value < scale[3]) {
    return scaleColor[2];
  } else if (value < scale[4]) {
    return scaleColor[3];
  } else {
    return scaleColor[4];
  }
}

function formattedData(value) {
  return `${Math.abs(value)} ${Math.abs(value) > 1 ? "jours" : "jour"} ${
    value < 0 ? "avant" : "après"
  }`;
}

function CovidWorldResponsePolicies() {
  const [policy, setPolicy] = useState("School_closing");
  const [step, setStep] = useState("10deaths");
  const [dataKey, setDataKey] = useState(getDataKey(policy, step));
  const mapRef = useRef(null);

  const scale = scales[dataKey];

  useEffect(() => setDataKey(getDataKey(policy, step)), [policy, step]);

  useEffect(() => {
    Object.keys(data).forEach((countryId) => {
      const el = mapRef.current.querySelector(`path[id="${countryId}"]`);
      if (el) {
        el.style.fill = getLocationColor(countryId, dataKey);
      }
    });
  }, [dataKey]);

  function getTooltipContent(countryId) {
    return {
      title: (
        <>
          <span className={`flag-icon flag-icon-${countryId.toLowerCase()}`} />
          {countries.getName(countryId, "fr")}
        </>
      ),
      mainContent: data[countryId] && data[countryId][dataKey] !== false && (
        <>
          Délai :{" "}
          <span>
            {data[countryId][dataKey] === null
              ? "Non adoptée"
              : formattedData(data[countryId][dataKey]) +
                " le " +
                steps.find((item) => item.value === step).label.toLowerCase()}
          </span>
        </>
      ),
      color: getLocationColor(countryId, dataKey),
      additionalContent: data[countryId] &&
        data[countryId][dataKey] !== false &&
        data[countryId][dataKey] !== null && (
          <TooltipList>
            <li>
              Adoption le{" "}
              {formatDate(data[countryId][`Date_${policy}`], "d MMMM yyyy")}
            </li>
            <li>
              Décès à cette date :{" "}
              {data[countryId][`ConfirmedDeaths_${policy}`]}
            </li>
            <li>Rang : {data[countryId][`rank_${step}_${policy}`]}</li>
          </TooltipList>
        ),
    };
  }

  return (
    <Container>
      <Inputs>
        <div>
          <Select
            label="Mesure contre le Covid-19 :"
            value={policy}
            onChange={(e) => setPolicy(e.target.value)}
          >
            {policies.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div>
          <Select
            label="Événement temporel de référence :"
            value={step}
            onChange={(e) => setStep(e.target.value)}
          >
            {steps.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </div>
      </Inputs>

      <Map
        map={World}
        containerRef={mapRef}
        getTooltipContent={getTooltipContent}
      />

      <Legend>
        <h1>Délai d'adoption de la mesure</h1>
        <p>
          Par rapport au franchissement du{" "}
          {steps.find((item) => item.value === step).label.toLowerCase()}
        </p>
        <ul>
          <li>
            <LegendColor color={scaleColor[0]} /> Entre{" "}
            {formattedData(scale[0])} et {formattedData(scale[1] - 1)}
          </li>
          <li>
            <LegendColor color={scaleColor[1]} /> Entre{" "}
            {formattedData(scale[1])} et {formattedData(scale[2] - 1)}
          </li>
          <li>
            <LegendColor color={scaleColor[2]} /> Entre{" "}
            {formattedData(scale[2])} et {formattedData(scale[3] - 1)}
          </li>
          <li>
            <LegendColor color={scaleColor[3]} /> Entre{" "}
            {formattedData(scale[3])} et {formattedData(scale[4] - 1)}
          </li>
          <li>
            <LegendColor color={scaleColor[4]} /> Entre{" "}
            {formattedData(scale[4])} et {formattedData(scale[5])}
          </li>

          <li>
            <LegendColor color={chroma(colors.borderColor).darken(0.15)} />{" "}
            Mesure non adoptée
          </li>
          <li>
            <LegendColor color={chroma(colors.borderColor).brighten(0.6)} /> Non
            référencé
          </li>
        </ul>
      </Legend>
    </Container>
  );
}

export default CovidWorldResponsePolicies;
